// src/components/VaccineAdjustmentList.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Badge,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import CustomDatePicker, { convertToThDate } from "./CustomDatePicker";

// เปิดหน้าจอมาแสดงย้อนหลังสามเดือน เพราะการปรับยอดเป็นงานที่ตามเรื่องกันในระยะสั้น
// ถ้าต้องการดูเอกสารเก่ากว่านั้นให้เลื่อนวันที่เริ่มต้นแล้วกดค้นหา
const getDefaultFilters = () => {
  const today = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 3);
  return {
    StartDate: startDate,
    EndDate: today,
    WarehouseID: "",
    ApprovalStatus: "",
    VaccineID: "",
    Keyword: "",
  };
};

// สถานะที่หน้าจอใช้คือ ApprovalStatus เพราะบอกได้ทั้งขั้นตอนและผลลัพธ์ในคอลัมน์เดียว
const approvalStatusInfo = {
  Pending: { label: "รออนุมัติ", bg: "#FFF3E0", color: "#D97706" },
  Approved: { label: "ปรับยอดแล้ว", bg: "#D1FAE5", color: "#047857" },
  Rejected: { label: "ไม่อนุมัติ", bg: "#FEE2E2", color: "#991B1B" },
  Cancelled: { label: "ยกเลิก", bg: "#F3F4F6", color: "#6B7280" },
  Archived: { label: "เอกสารเก่า", bg: "#EDE9FE", color: "#5B21B6" },
};

const getApprovalStatusInfo = (status) =>
  approvalStatusInfo[status] || { label: status || "-", bg: "#EDF2F7", color: "#4A5568" };

const formatDate = (value) => convertToThDate(value) || "-";

const ApprovalStatusBadge = ({ status }) => {
  const info = getApprovalStatusInfo(status);
  return (
    <Box
      bg={info.bg}
      color={info.color}
      borderColor={info.color}
      borderWidth={1}
      borderRadius="full"
      px={2}
      py={0.5}
      textAlign="center"
      fontSize="xs"
      fontWeight="semibold"
      lineHeight="short"
      display="inline-block"
      whiteSpace="nowrap"
    >
      {info.label}
    </Box>
  );
};

// เอกสารหนึ่งใบมีได้หลายวัคซีนหลาย Lot ถ้าพิมพ์ออกมาทั้งหมดตารางจะยืดจนอ่านยาก
// จึงแสดงชนิดแรกแล้วบอกจำนวนที่เหลือ ส่วนรายชื่อครบอยู่ในคำแนะนำเมื่อชี้เมาส์
const vaccineNamesOf = (row) => [
  ...new Set((row.Vaccines || []).map((item) => item.VaccineNameTH || item.VaccineID)),
];

const VaccineCell = ({ row }) => {
  const names = vaccineNamesOf(row);
  const lotCount = (row.Vaccines || []).length;

  if (names.length === 0) {
    return (
      <Text fontSize="sm" color="gray.400">
        ไม่มีรายการ
      </Text>
    );
  }

  return (
    <Tooltip label={`${names.join(", ")} · รวม ${lotCount} Lot`}>
      <Box py={1}>
        <Text fontSize="sm" noOfLines={2}>
          {names[0]}
        </Text>
        {names.length > 1 && (
          <Text fontSize="xs" color="gray.500">
            และอีก {names.length - 1} ชนิด
          </Text>
        )}
      </Box>
    </Tooltip>
  );
};

// ต่ำกว่า lg ตารางจะแน่นเกินไป จึงสลับไปใช้การ์ดแทน แบบเดียวกับหน้ารายการจำหน่ายวัคซีน
const MobileAdjustmentList = ({ data, onOpenDetail }) => (
  <VStack spacing={3} align="stretch" w="100%">
    {data.map((row) => (
      <Box
        key={row.TransactionID}
        w="100%"
        p={{ base: 3, sm: 4 }}
        borderWidth={1}
        borderRadius="lg"
        boxShadow="sm"
      >
        <VStack align="start" spacing={3}>
          <Flex w="100%" justify="space-between" align="flex-start" gap={2} minW={0}>
            <VStack align="start" spacing={1} minW={0} flex="1">
              <Text fontWeight="bold" fontSize="md" noOfLines={2}>
                {row.TransactionID}
              </Text>
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {row.WarehouseName || row.WarehouseID || "-"}
              </Text>
            </VStack>
            <VStack align="end" spacing={2} flexShrink={0}>
              <ApprovalStatusBadge status={row.ApprovalStatus} />
              <IconButton.ViewIcon
                size="sm"
                aria-label="ดูรายละเอียด"
                title="ดูรายละเอียด"
                onClick={() => onOpenDetail(row.TransactionID)}
              />
            </VStack>
          </Flex>

          <Box w="100%" pt={2} borderTopWidth="1px" borderColor="gray.200">
            <Grid templateColumns="auto 1fr" gapX={2} gapY={1} fontSize="sm" color="gray.600">
              <GridItem fontWeight="medium">วันที่ขอปรับ:</GridItem>
              <GridItem>{formatDate(row.RequestDate)}</GridItem>

              <GridItem fontWeight="medium">วัคซีน:</GridItem>
              <GridItem>
                {vaccineNamesOf(row).join(", ") || "ไม่มีรายการ"}
              </GridItem>

              <GridItem fontWeight="medium">เหตุผล:</GridItem>
              <GridItem>{row.RequestType || "-"}</GridItem>

              <GridItem fontWeight="medium">ผู้ยื่นคำขอ:</GridItem>
              <GridItem>{row.RequesterID || "-"}</GridItem>
            </Grid>
          </Box>
        </VStack>
      </Box>
    ))}
  </VStack>
);

const VaccineAdjustmentList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();

  // ต่ำกว่า lg ตารางจะแน่นเกินไป จึงสลับไปใช้การ์ดแทน
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });

  const today = new Date();

  const [adjustments, setAdjustments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [isInventoryAdmin, setIsInventoryAdmin] = useState(false);
  // ยอดรออนุมัติทั้งหมด นับทุกช่วงเวลา ไม่ใช่เฉพาะที่แสดงอยู่
  const [pendingTotal, setPendingTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [filterData, setFilterData] = useState(getDefaultFilters);
  // ช่วงวันที่ที่แสดงอยู่จริง อัปเดตเมื่อค้นหาสำเร็จ ไม่ใช่ตอนแก้ช่องวันที่
  const [appliedRange, setAppliedRange] = useState(() => {
    const initial = getDefaultFilters();
    return { StartDate: initial.StartDate, EndDate: initial.EndDate };
  });

  const minDateForEndDate = filterData.StartDate ? new Date(filterData.StartDate) : undefined;

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  // ใช้ minWidth แทน width เพื่อให้คอลัมน์ยืดตามจอกว้างได้
  // และซ่อนคอลัมน์ที่จำเป็นน้อยกว่าเมื่อจอแคบลง เรียงจากซ่อนก่อนไปหลัง
  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      minWidth: "230px",
      wrap: true,
    },
    {
      name: "วันที่ขอปรับ",
      selector: (row) => row.RequestDate || "",
      format: (row) => formatDate(row.RequestDate),
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "คลัง",
      selector: (row) => row.WarehouseName || row.WarehouseID,
      sortable: true,
      minWidth: "180px",
      wrap: true,
    },
    {
      name: "วัคซีนที่ขอปรับ",
      selector: (row) => row.VaccineSummary || "",
      cell: (row) => <VaccineCell row={row} />,
      sortable: true,
      minWidth: "220px",
      wrap: true,
      hide: 1200,
    },
    {
      name: "เหตุผลที่ขอปรับ",
      selector: (row) => row.RequestType || "-",
      sortable: true,
      minWidth: "160px",
      wrap: true,
      hide: 1400,
    },
    {
      name: "ผู้ยื่นคำขอ",
      selector: (row) => row.RequesterID || "-",
      sortable: true,
      minWidth: "160px",
      wrap: true,
      hide: 1600,
    },
    {
      name: "สถานะ",
      selector: (row) => row.ApprovalStatus,
      sortable: true,
      cell: (row) => <ApprovalStatusBadge status={row.ApprovalStatus} />,
      minWidth: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccineadjustment/${row.TransactionID}`)}
          label={
            row.ApprovalStatus === "Pending" && isInventoryAdmin ? "ตรวจและยืนยัน" : "ดูรายละเอียด"
          }
          size="sm"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "160px",
    },
  ];

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentWarehouses",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  }, [sessionId]);

  const fetchVaccineOptions = useCallback(async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  }, [sessionId]);

  // การกรองทำที่ฝั่งเซิร์ฟเวอร์ทั้งหมด เพื่อไม่ต้องดึงเอกสารทุกใบมาไว้ที่หน้าจอ
  const fetchAdjustments = useCallback(
    async (filters) => {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await apiService({
          method: "POST",
          url: "/getVaccineAdjustmentsList",
          data: {
            StartDate: filters.StartDate || "",
            EndDate: filters.EndDate || "",
            WarehouseID: filters.WarehouseID || "",
            ApprovalStatus: filters.ApprovalStatus || "",
            VaccineID: filters.VaccineID || "",
            Keyword: filters.Keyword?.trim() || "",
          },
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          setAdjustments(response.VaccineAdjustmentsList || []);
          setIsInventoryAdmin(Boolean(response.IsInventoryAdmin));
          setPendingTotal(Number(response.PendingTotal) || 0);
          setAppliedRange({ StartDate: filters.StartDate, EndDate: filters.EndDate });
        } else {
          setAdjustments([]);
          setLoadError(response.error || "ไม่สามารถดึงข้อมูลได้");
        }
      } catch (error) {
        console.error("Error fetching adjustments:", error);
        setAdjustments([]);
        setLoadError(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
      fetchVaccineOptions();
      fetchAdjustments(getDefaultFilters());
    }
  }, [sessionId, fetchWarehouses, fetchVaccineOptions, fetchAdjustments]);

  const pendingCount = useMemo(
    () => adjustments.filter((row) => row.ApprovalStatus === "Pending").length,
    [adjustments]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchAdjustments(filterData);
  };

  const handleClearSearch = () => {
    const resetFilters = getDefaultFilters();
    setFilterData(resetFilters);
    fetchAdjustments(resetFilters);
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 5 }} align="start">
        <StandardHeader
          title="รายการปรับยอดวัคซีนคงคลัง"
          bgColor="iBlue.600"
          icon={faFileLines}
          size="sm"
        />

        <HStack w="100%" spacing={3} flexWrap="wrap">
          {pendingTotal > 0 && (
            <Tooltip
              label={
                pendingTotal > pendingCount
                  ? `อยู่ในช่วงที่แสดง ${pendingCount} ใบ อีก ${pendingTotal - pendingCount} ใบอยู่นอกช่วงวันที่ที่ค้นหา`
                  : "ทุกใบที่รออนุมัติอยู่ในช่วงที่แสดงแล้ว"
              }
            >
              <Badge colorScheme="orange" borderRadius="full" px={3} py={1} fontSize="sm">
                รออนุมัติทั้งหมด {pendingTotal} ใบ
                {pendingTotal > pendingCount ? ` (แสดงอยู่ ${pendingCount} ใบ)` : ""}
              </Badge>
            </Tooltip>
          )}
          {isInventoryAdmin && (
            <Tooltip label="คุณมีสิทธิ์ตรวจและยืนยันการปรับยอดของทุกคลัง">
              <Badge colorScheme="purple" borderRadius="full" px={3} py={1} fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faShieldHalved} />
                  <span>ผู้ดูแลระบบคลังวัคซีน</span>
                </HStack>
              </Badge>
            </Tooltip>
          )}
        </HStack>

        <Text fontSize="sm" color="gray.600">
          การปรับยอดจะกันจำนวนที่ขอลดไว้ตั้งแต่ยื่นคำขอ จนกว่าผู้ดูแลระบบจะยืนยันหรือไม่อนุมัติ
          ระหว่างนั้นคลังยังเบิกจ่ายส่วนที่เหลือได้ตามปกติ
        </Text>

        {/* บอกช่วงที่กำลังแสดงอยู่ เพราะค่าเริ่มต้นเปิดมาเห็นแค่สามเดือนหลังสุด */}
        <Text fontSize="sm" color="gray.500">
          กำลังแสดงคำขอที่มีวันที่ขอปรับระหว่าง {convertToThDate(appliedRange.StartDate)} ถึง{" "}
          {convertToThDate(appliedRange.EndDate)} จำนวน {adjustments.length} ใบ
        </Text>

        {/* จอแคบวางตัวกรองเรียงลงมาทีละช่อง จอกว้างเรียงเป็นแถวเดียว */}
        <Box w="100%" borderWidth={1} borderRadius="lg" boxShadow="sm" p={{ base: 3, md: 4 }}>
          <StandardHeader
            title="ค้นหาคำขอปรับยอด"
            bgColor="iBlue.600"
            icon={faSearch}
            size="sm"
          />
          <Grid
            w="100%"
            minW={0}
            gap={3}
            mt={3}
            templateColumns={{
              base: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "auto repeat(3, minmax(0, 1fr))",
            }}
          >
            {/* ช่องวันที่กว้างตามเนื้อหา จึงจับคู่ไว้ในช่องเดียวกันให้อยู่ติดกัน */}
            <Flex gap={3} wrap="wrap" align="flex-end">
              <FormControl w="auto">
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  วันที่ขอปรับ ตั้งแต่
                </FormLabel>
                <CustomDatePicker
                  name="StartDate"
                  value={filterData.StartDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxDate={today}
                />
              </FormControl>

              <FormControl w="auto">
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  ถึงวันที่
                </FormLabel>
                <CustomDatePicker
                  name="EndDate"
                  value={filterData.EndDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  minDate={minDateForEndDate}
                  maxDate={today}
                />
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                คำค้น
              </FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faSearch} color="gray" />
                </InputLeftElement>
                <Input
                  name="Keyword"
                  placeholder="เลขที่เอกสาร เหตุผล ผู้ยื่น วัคซีน หรือ Lot"
                  value={filterData.Keyword}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                คลัง
              </FormLabel>
              <Select
                name="WarehouseID"
                placeholder="ทุกคลัง"
                value={filterData.WarehouseID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option key={warehouse.WarehouseID} value={warehouse.WarehouseID}>
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                สถานะ
              </FormLabel>
              <Select
                name="ApprovalStatus"
                placeholder="ทุกสถานะ"
                value={filterData.ApprovalStatus}
                onChange={handleInputChange}
                colorScheme="iBlue"
              >
                {Object.entries(approvalStatusInfo).map(([value, info]) => (
                  <option key={value} value={value}>
                    {info.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                วัคซีนที่ขอปรับ
              </FormLabel>
              <Select
                name="VaccineID"
                placeholder="ทุกวัคซีน"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* ปุ่มอยู่ในช่องสุดท้ายของกริด เพื่อไม่ให้เปลืองอีกหนึ่งแถว */}
            <Flex
              gap={3}
              align="flex-end"
              justify="flex-end"
              gridColumn={{ base: "auto", md: "1 / -1", xl: "2 / -1" }}
            >
              <IconButton.Clear onClick={handleClearSearch} />
              <IconButton.Search onClick={handleSearch} isLoading={isLoading} />
            </Flex>
          </Grid>
        </Box>

        <Flex w="100%" justify="flex-end">
          <IconButton.Add
            onClick={() => navigate("/vaccineadjustment/new")}
            label="ยื่นคำขอปรับยอด"
            w={{ base: "100%", sm: "auto" }}
          />
        </Flex>

        {loadError ? (
          <Text fontSize="lg" color="red.500" textAlign="center" w="100%">
            {loadError}
          </Text>
        ) : isLoading ? (
          <Flex justify="center" w="100%" py={10}>
            <Spinner size="xl" />
          </Flex>
        ) : adjustments.length === 0 ? (
          <VStack w="100%" spacing={1} py={6}>
            <Text fontSize="lg" color="gray.500" textAlign="center">
              ไม่พบคำขอปรับยอดในช่วง {convertToThDate(appliedRange.StartDate)} ถึง{" "}
              {convertToThDate(appliedRange.EndDate)}
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              หากต้องการดูเอกสารเก่ากว่านี้ ให้เลื่อนวันที่เริ่มต้นแล้วกดค้นหา
            </Text>
          </VStack>
        ) : isMobile ? (
          <MobileAdjustmentList
            data={adjustments}
            onOpenDetail={(transactionId) => navigate(`/vaccineadjustment/${transactionId}`)}
          />
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={adjustments}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineAdjustmentList;
